import { type FormikErrors } from "formik";

import { BTLocalStorage } from "types/btStorage";

import { type ISetLoadingActionOptions } from "utilities/form/form";
import { routes } from "utilities/routes";

import { type IAuthStoreDiscoveryHandler } from "commonComponents/auth/AuthStoreDiscovery.api.handler";
import {
    type AuthStoreDiscoveryActionType,
    AuthStoreTypes,
    type IAuthStoreDiscoveryFormValues,
    type IOAuthStateParamState,
} from "commonComponents/auth/AuthStoreDiscovery.types";
import { dismissMfaNudge } from "commonComponents/utilities/MfaEnrollmentNudgeBanner/MfaEnrollmentNudgeBanner.storage";

export const redirectToAuth0User = (
    userName: string,
    oauthStateParam: IOAuthStateParamState | null,
) => {
    BTLocalStorage.set("bt-object-oAuthStateParam", oauthStateParam);
    const previousBuilderId = BTLocalStorage.get("bt-number-selectedBuilderId");

    const baseUrl = `/api/Login/LoginWithAuth0?username=${encodeURIComponent(userName)}`;
    const stateParam = oauthStateParam ? `&state=${oauthStateParam.stateIdentifier}` : "";
    const builderParam = previousBuilderId ? `&preferredBuilderId=${previousBuilderId}` : "";
    const url = baseUrl + stateParam + builderParam;
    window.location.assign(url);
};

/**
 * Logs the current user out and redirects to Auth0 login with every-login MFA opt-in.
 *
 * Navigates to the logout endpoint with a post-logout redirect back to BT's `LoginWithAuth0`
 * endpoint carrying `optInEveryLogin=true`. The backend turns that flag into the Auth0
 * `MfaRequirement=EveryLogin` param so the re-login prompts MFA enrollment every time.
 *
 * Shared by all MFA enrollment "Enroll now" CTAs.
 */
export const optInEveryLoginAndLogout = (userId: number | null) => {
    // Mark the nudge dismissed before logging out: the user has acted on the nudge, so it must
    // not reappear when they return after enrolling (the backend nudge state lags one login).
    // Scope the dismissal to the current user; never write an unscoped entry that would leak to a
    // different user on the same machine.
    if (userId != null) {
        dismissMfaNudge(userId);
    }
    // Empty username: the post-logout re-login resolves the user via the active Auth0 session,
    // and no OAuth state is needed for the opt-in flow.
    const loginRedirectUrl = "/api/Login/LoginWithAuth0?username=&optInEveryLogin=true";
    // getLogoutAndRedirectLink encodes redirectUrl, so pass the inner URL unencoded.
    window.location.assign(routes.logout.getLogoutAndRedirectLink(loginRedirectUrl));
};

export const redirectToAuth0Activation = (
    userName: string,
    oauthStateParam: IOAuthStateParamState,
) => {
    BTLocalStorage.set("bt-object-oAuthStateParam", oauthStateParam);

    const url = `/api/Users/Activation?username=${encodeURIComponent(userName)}&shareToken=${
        oauthStateParam?.shareToken
    }&state=${oauthStateParam.stateIdentifier}`;
    window.location.assign(url);
};

export const handleRedirect = async (
    discoveryHandler: IAuthStoreDiscoveryHandler,
    username: string,
    shareToken: string,
    recaptchaToken: string,
    setErrors: (errors: FormikErrors<IAuthStoreDiscoveryFormValues>) => void,
    redirectToBTUser: (username: string) => void,
) => {
    const resp = await discoveryHandler?.getAuthStoreForUsername(username, recaptchaToken);

    if (!resp?.userExists) {
        setErrors({ username: "User does not exist" });
    } else if (resp?.authStore === AuthStoreTypes.Auth0) {
        const stateIdentifier = crypto.randomUUID().toUpperCase();
        const oAuthState: IOAuthStateParamState = {
            stateIdentifier: stateIdentifier,
            shareToken: shareToken,
        };

        redirectToAuth0User(username, oAuthState);
    } else {
        redirectToBTUser(username);
    }
};

export async function handleLinkInternal(
    values: IAuthStoreDiscoveryFormValues,
    setErrors: (errors: FormikErrors<IAuthStoreDiscoveryFormValues>) => void,
    setActionBeingPerformed: ({
        actionBeingPerformed,
        callback,
        callbackNavigates,
    }: ISetLoadingActionOptions<AuthStoreDiscoveryActionType>) => Promise<void>,
    authStoreDiscoveryHandler: IAuthStoreDiscoveryHandler,
    showAPIErrorMessage: (e: unknown, customMessage?: string) => void,
    redirectToBTUser: (username: string) => void,
    shareToken: string,
) {
    try {
        await setActionBeingPerformed({
            actionBeingPerformed: "submit",
            callback: async () => {
                await handleRedirect(
                    authStoreDiscoveryHandler,
                    values.username,
                    shareToken,
                    values.recaptchaToken!,
                    setErrors,
                    redirectToBTUser,
                );
            },
            callbackNavigates: true,
        });
    } catch (e) {
        showAPIErrorMessage(e);
    }
}
